import { useEffect, useRef, useCallback } from 'react';
import { useLocalStorage, useDebounceCallback } from 'usehooks-ts';
import { useBootstrapConfig } from '@/Domain/components/App/BootstrapProvider';
import { usePreview } from '@/Domain/components/PreviewProvider';
import { cn } from '@/Domain/utils/cn';
import {
  CHAT_PAGE_ACCESS_TOKEN_KEY,
  getCurrentScenarioId,
  getPreviewIdentityToken,
} from '@/context';

/**
 * Parses a metadata JSON string and validates it's a non-null, non-array object.
 * Returns null if invalid.
 */
export const parseMetadataString = (metadataStr: string): Record<string, unknown> | null => {
  if (!metadataStr.trim()) return null;
  try {
    const parsed = JSON.parse(metadataStr);
    if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
};

/**
 * Builds the params JSON for the widget iframe, merging METADATA_OVERRIDE_ prefixed
 * metadata with the identity token (unprefixed, so the backend can extract it).
 */
export const buildWidgetParams = (
  overrideMetadata: Record<string, unknown> | null,
  identityToken: string | null,
): string | undefined => {
  const params: Record<string, unknown> = {};
  if (overrideMetadata) {
    Object.assign(params, overrideMetadata);
  }
  if (identityToken) {
    params[CHAT_PAGE_ACCESS_TOKEN_KEY] = identityToken;
  }
  return Object.keys(params).length > 0 ? JSON.stringify(params) : undefined;
};

/**
 * Transforms metadata object by prefixing all keys with METADATA_OVERRIDE_.
 * Also marks removed keys (present in previousKeys but not in current metadata) with null.
 */
export const transformMetadataForWidget = (
  metadata: Record<string, unknown>,
  previousKeys: Set<string>,
): { overrideMetadata: Record<string, unknown>; currentKeys: Set<string> } => {
  const overrideMetadata: Record<string, unknown> = {};
  const currentKeys = new Set<string>();

  for (const [key, value] of Object.entries(metadata)) {
    overrideMetadata[`METADATA_OVERRIDE_${key}`] = value;
    currentKeys.add(key);
  }

  // Send null for keys that were previously sent but are no longer present
  for (const previousKey of previousKeys) {
    if (!currentKeys.has(previousKey)) {
      overrideMetadata[`METADATA_OVERRIDE_${previousKey}`] = null;
    }
  }

  return { overrideMetadata, currentKeys };
};

type Props = {
  className?: string;
};

export const AiWidget = ({ className }: Props) => {
  const { iframe, iframeElement, setIframeElement, getValidatedMetadata, metadata } = usePreview();
  const { apiServerUrl, apiKey } = useBootstrapConfig();
  const scenario = getCurrentScenarioId();
  const localStorageItemName = `__qc_conversation_encrypted_${scenario}`;
  const [convId, setConversation, removeConversation] = useLocalStorage(localStorageItemName, null);
  const metadataInitializedRef = useRef(false);
  const lastSentMetadataRef = useRef<string | null>(null);
  const lastSentKeysRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'conversation') {
        const conversation = JSON.parse(event.data.data);

        setConversation(conversation.conv_id);
      }
      if (event.data.type === 'reset') {
        removeConversation();
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [setConversation, removeConversation]);

  useEffect(() => {
    if (!iframeElement) {
      const validatedMetadata = getValidatedMetadata();
      // Prefix all keys with METADATA_OVERRIDE_ so they override existing metadata
      let overrideMetadata: Record<string, unknown> | null = null;
      if (validatedMetadata) {
        overrideMetadata = {};
        for (const [key, value] of Object.entries(validatedMetadata)) {
          overrideMetadata[`METADATA_OVERRIDE_${key}`] = value;
        }
      }
      const params = buildWidgetParams(overrideMetadata, getPreviewIdentityToken());

      const urlParams = new URLSearchParams({
        scenario_id: scenario,
        server_url: apiServerUrl,
        source: 'preview',
        standalone: 'true',
        ...(convId ? { encrypted_conversation_id: convId } : {}),
        ...(apiKey ? { api_key: apiKey } : {}),
        ...(params ? { params } : {}),
      });

      const iframeJsx = (
        <iframe
          key="ai-preview-iframe"
          ref={iframe}
          src={`https://widget.quickchat.ai/messages.html?${urlParams.toString()}`}
          className="w-full border border-border rounded-3xl !bg-background/85 h-full max-h-[80rem] max-w-[75rem] !shadow-lg"
          referrerPolicy="strict-origin"
          allow="microphone; local-network-access"
        />
      );
      setIframeElement(iframeJsx);
      metadataInitializedRef.current = true;
      const initialMetadata = getValidatedMetadata();
      if (initialMetadata) {
        lastSentMetadataRef.current = JSON.stringify(initialMetadata);
        lastSentKeysRef.current = new Set(Object.keys(initialMetadata));
      }
    }
  }, [iframeElement, setIframeElement, convId, getValidatedMetadata]);

  // Send metadata override to widget via postMessage when metadata changes
  // Debounced to avoid sending on every keystroke (which would save partial values like "a", "ab", "abc")
  // No iframe reload needed - widget will include this in future API requests
  const sendMetadataToWidget = useCallback(
    (metadataStr: string) => {
      if (!metadataInitializedRef.current || !iframeElement || !iframe.current) return;

      const validatedMetadata = parseMetadataString(metadataStr);

      if (validatedMetadata) {
        const currentMetadataStr = JSON.stringify(validatedMetadata);
        if (lastSentMetadataRef.current === currentMetadataStr) return;

        const { overrideMetadata, currentKeys } = transformMetadataForWidget(
          validatedMetadata,
          lastSentKeysRef.current,
        );

        // Include identity token so it persists across metadata updates
        const metadataWithToken: Record<string, unknown> = { ...overrideMetadata };
        if (getPreviewIdentityToken()) {
          metadataWithToken[CHAT_PAGE_ACCESS_TOKEN_KEY] = getPreviewIdentityToken();
        }

        // Send metadata update via postMessage to widget
        iframe.current.contentWindow?.postMessage(
          { type: 'update_metadata', metadata: metadataWithToken },
          'https://widget.quickchat.ai',
        );
        lastSentMetadataRef.current = currentMetadataStr;
        lastSentKeysRef.current = currentKeys;
      }
    },
    [iframe, iframeElement],
  );

  const debouncedSendMetadata = useDebounceCallback(sendMetadataToWidget, 500);

  useEffect(() => {
    // Pass metadata directly to avoid stale closure issues with debouncing
    debouncedSendMetadata(metadata);
  }, [metadata, debouncedSendMetadata]);

  return (
    <div
      className={cn(
        'w-full h-full z-10 flex items-center start 2xl:justify-center [&_iframe]:h-[calc(100vh-140px)] [&_iframe]:max-h-[60rem] [&_iframe]:max-w-[80rem]',
        className,
      )}
    >
      {iframeElement}
    </div>
  );
};
